import { FC, Fragment, useEffect, useMemo, useState } from 'react'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { EmbedSessionError } from 'components/pages/PurchaseFlow/_main/EmbedSessionError/EmbedSessionError'
import { getBlockedEmbedAncestorOrigin, readEmbedFrameContext } from 'utils/embed/embedParentOrigin'
import { logAnalyticsEvent } from 'utils/analytics'
import { embedSession } from 'utils/embedSession'
import { logger } from 'utils/logging'
import { useEmbedIsOneSystem } from 'utils/embedSession/useEmbedIsOneSystem'
import { useEmbedSessionBootstrap } from 'utils/embedSession/useEmbedSessionBootstrap'

interface Props {
  component: FC
  /** Analytics event fired once on an authenticated mount */
  entryEvent: string
  /** Logging identity of the concrete guard usage */
  componentName: string
}

/** Shared guard for the CRM embed surfaces: allowlisted parent origin + broker session, or EmbedSessionError. */
export const EmbedGuard: FC<Props> = ({ component: Component, entryEvent, componentName }) => {
  const { status } = useEmbedSessionBootstrap()

  const isOneSystem = useEmbedIsOneSystem()
  // Defense in depth behind the frame-ancestors CSP header — see embedParentOrigin.ts
  // Pinned at mount: the onOffice entry opens its session before navigating here, a Propstack one is still pending.
  // Read live, a cleared onOffice session would recheck crm.onoffice.de against Propstack's allowlist; a route change
  // that remounts the guard after a failed refresh keeps the CRM of the lost session.
  const [provider] = useState(() => embedSession.getProvider() ?? embedSession.getLastProvider() ?? 'propstack')
  const blockedOrigin = useMemo(() => getBlockedEmbedAncestorOrigin(readEmbedFrameContext(provider)), [provider])

  useEffect(() => {
    if (!blockedOrigin) return
    logger.warn('Embed rendered from an unauthorized parent origin', undefined, {
      context: { component: componentName, action: 'blockedParentOrigin', metadata: { origin: blockedOrigin, provider } },
    })
    logAnalyticsEvent(AnalyticsEvent.EMBED_BLOCKED_PARENT_ORIGIN, { origin: blockedOrigin })
    // Blocked-origin telemetry is session-independent — keyed on blockedOrigin (stable) so a
    // session state flip after mount cannot double-count the signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedOrigin])

  useEffect(() => {
    if (blockedOrigin) return
    if (status === 'active') {
      logAnalyticsEvent(entryEvent, { revenueChannel: provider === 'onoffice' ? 'ONOFFICE' : 'PROPSTACK', isOneSystem })
    } else if (status === 'error') {
      logger.warn('Embed surface rendered with no session', undefined, {
        context: { component: componentName, action: 'sessionError', metadata: { provider } },
      })
    }
    // Fire once per resolved session state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  return (
    <Fragment>
      {!blockedOrigin && status === 'active' && <Component />}
      {(blockedOrigin || status === 'error') && <EmbedSessionError provider={provider} />}
      {/* pending (and not blocked): render nothing — the exchange resolves in well under a second */}
    </Fragment>
  )
}
